import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";

const FilePreview = ({ attachment, disabled, onRemove }) => {
    const [previewUrl, setPreviewUrl] = useState("");

    useEffect(() => {
        const url = URL.createObjectURL(attachment.file);
        setPreviewUrl(url);

        return () => URL.revokeObjectURL(url);
    }, [attachment.file]);

    const isPdf = attachment.file.type === "application/pdf";

    return (
        <div className="cash-attachment-card">
            <a
                href={previewUrl || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="cash-attachment-preview"
                aria-label={`Preview ${attachment.file.name}`}
            >
                {isPdf ? (
                    <i
                        className="fa fa-file-pdf-o text-danger"
                        aria-hidden="true"
                    />
                ) : (
                    previewUrl && (
                        <img
                            src={previewUrl}
                            alt={attachment.file.name}
                        />
                    )
                )}
            </a>

            <div
                className="cash-attachment-name"
                title={attachment.file.name}
            >
                {attachment.file.name}
            </div>

            <button
                type="button"
                className="cash-attachment-delete"
                disabled={disabled}
                onClick={() => onRemove(attachment)}
                aria-label={`Remove ${attachment.file.name}`}
            >
                <i className="fa fa-trash" aria-hidden="true" />
            </button>
        </div>
    );
};

const CashEntryAttachments = ({
    attachments = [],
    disabled = false,
    onChange,
    inputRef,
    onRemoveRow,
    canRemoveRow = false
}) => {
    const nextId = useRef(1);

    const selectFiles = async (event) => {
        const selectedFiles = Array.from(event.target.files || []);

        event.target.value = "";

        if (disabled || selectedFiles.length === 0) return;

        const acceptedFiles = selectedFiles.filter(
            (file) =>
                file.type === "application/pdf" ||
                file.type.startsWith("image/")
        );

        const newAttachments = acceptedFiles
            .filter(
                (file) =>
                    !attachments.some(
                        (attachment) =>
                            attachment.file.name === file.name &&
                            attachment.file.size === file.size &&
                            attachment.file.lastModified === file.lastModified
                    )
            )
            .map((file) => ({
                id: nextId.current++,
                file
            }));

        if (newAttachments.length > 0) {
            onChange([...attachments, ...newAttachments]);
        }

        if (acceptedFiles.length !== selectedFiles.length) {
            await Swal.fire(
                "Unsupported File",
                "Only PDF and image files are allowed.",
                "warning"
            );
        }
    };

    const removeFile = async (attachment) => {
        if (disabled) return;

        const result = await Swal.fire({
            title: "Remove attachment?",
            text: attachment.file.name,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, remove",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc3545"
        });

        if (result.isConfirmed) {
            onChange(
                attachments.filter(
                    (item) => item.id !== attachment.id
                )
            );
        }
    };

    return (
        <div className="cash-attachments">
            <input
                ref={inputRef}
                type="file"
                accept="application/pdf,image/*"
                multiple
                hidden
                disabled={disabled}
                onChange={selectFiles}
            />

            <div className="cash-attachments-toolbar">
                <div className="cash-attachments-toolbar-left">
                    <span>
                        <i className="fa fa-paperclip me-1" aria-hidden="true" />
                        Attachments ({attachments.length})
                    </span>

                    <button
                        type="button"
                        className="btn btn-outline-primary erp-small-button"
                        disabled={disabled}
                        onClick={() => inputRef.current?.click()}
                    >
                        <i className="fa fa-plus me-1" aria-hidden="true" />
                        Browse
                    </button>

                    <small className="text-muted">PDF or Images</small>
                </div>

                <button
                    type="button"
                    className="btn btn-danger erp-row-remove cash-entry-delete"
                    disabled={
                        disabled ||
                        !canRemoveRow ||
                        typeof onRemoveRow !== "function"
                    }
                    onClick={onRemoveRow}
                    title="Delete entry row"
                    aria-label="Delete entry row"
                >
                    <i className="fa fa-trash" aria-hidden="true" />
                </button>
            </div>

            {attachments.length > 0 && (
                <div className="cash-attachment-list">
                    {attachments.map((attachment) => (
                        <FilePreview
                            key={attachment.id}
                            attachment={attachment}
                            disabled={disabled}
                            onRemove={removeFile}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export const CashEntryAttachmentSection = ({
    row,
    disabled,
    updateRow,
    onRemoveRow,
    canRemoveRow = false
}) => {
    const inputRef = useRef(null);

    return (
        <>
            <div className="form-group col-12 col-md-3">
                <label
                    htmlFor={`cash-in-remark-${row.id}`}
                    className="form-label"
                >
                    Remark
                </label>

                <input
                    id={`cash-in-remark-${row.id}`}
                    type="text"
                    className="form-control"
                    placeholder="Enter remark"
                    value={row.remark || ""}
                    disabled={disabled}
                    onChange={(event) =>
                        updateRow(row.id, "remark", event.target.value)
                    }
                />
            </div>

            <div className="col-12 order-last">
                <CashEntryAttachments
                    inputRef={inputRef}
                    attachments={row.attachments || []}
                    disabled={disabled}
                    onChange={(attachments) =>
                        updateRow(row.id, "attachments", attachments)
                    }
                    onRemoveRow={onRemoveRow}
                    canRemoveRow={canRemoveRow}
                />
            </div>
        </>
    );
};

export default CashEntryAttachments;